import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { Link, Switch, Route } from 'react-router-dom'

import { map, get, omit, compact, includes } from 'lodash'

import { translate } from 'parking-react-common'

import { ui, routing, api, parkingLots, payments, gates, sessions, privileges, events, promoCodes } from '../redux'

import NavBar from '../components/NavBar'
import TabBar from '../components/TabBar'
import Panel from '../components/Panel'
import Button from '../components/Button'

import ScheduleDetails from '../components/ScheduleDetails'
import ScheduleForm from '../components/ScheduleForm'
import DetailedSessionsList from '../components/DetailedSessionsList'
import EventsList from '../components/EventsList'
import PaymentsList from '../components/PaymentsList'
import PrivilegesList from '../components/PrivilegesList'
import PrivilegeForm from '../components/PrivilegeForm'
import GatesList from '../components/GatesList'

import ParkingLotBasicInfo from '../components/ParkingLotBasicInfo'
import ParkingLotBasicInfoForm from '../components/ParkingLotBasicInfoForm'
import ParkingLotMapForm from '../components/ParkingLotMapForm'
import ParkingLotMapView from '../components/ParkingLotMapView'
import ParkingLotPhotoView from '../components/ParkingLotPhotoView'
import PromoCodesList from '../components/PromoCodesList'
import PromoCodesForm from './PromoCodesForm'

const EDITABLE_SECTIONS = ['basic-info', 'schedule', 'location', 'photo']

const tabRoute = (id, tab, params = {}) => {
    const action = includes(EDITABLE_SECTIONS, tab) ? params.action : null
    return routing.routeFor('parkingLots', { id, tab, ...params, action })
}

const ParkingLotDetails = ({ item, tab, action, canEdit, t }) => {
    if (!item) {
        return null
    }

    const tabs = compact([
        { key: 'basic-info', title: t('Basic Info') },
        { key: 'schedule',   title: t('Schedule and Pricing') },
        { key: 'privileges', title: t('Privileges') },
        { key: 'sessions',   title: t('Sessions') },
        { key: 'payments',   title: t('Payments') },
        parkingLots.isPrivate(item)
            ? { key: 'gates', title: t('Gates') }
            : null,
        parkingLots.isPrivate(item)
            ? { key: 'events', title: t('Events') }
            : null,
        { key: 'location',   title: t('Location') },
        { key: 'photo',      title: t('Photo') },
        { key: 'promo-codes', title: t('Promo Codes') }
    ])


    const { id, url, name } = item
    const isEditing = canEdit && action === 'edit'

    const pageTitle = <Link to={ routing.routeFor('parkingLots') }>{ t('Parking Lots') }</Link>

    const itemWithoutSchedule = omit(item, 'opening_intervals')

    return (
        <div>
            <NavBar title={ pageTitle } />
            <Panel
                title={ name }
                hasTabs
                tools={ (
                    canEdit && includes(EDITABLE_SECTIONS, tab) && (
                        isEditing
                            ? (
                                <Button
                                    label={ t('Discard') }
                                    size={ ui.SIZE.large }
                                    intent={ ui.INTENT.default }
                                    link={ tabRoute(id, tab, { action: null }) }
                                    icon="md/clear"
                                />
                            )
                            : (
                                <Button
                                    label={ t('Edit') }
                                    size={ ui.SIZE.large }
                                    intent={ ui.INTENT.warning }
                                    link={ tabRoute(id, tab, { action: 'edit' }) }
                                    icon="md/edit"
                                />
                            )
                    )
                ) }
            >
                <TabBar tabs={ map(tabs, (tab) => ({ ...tab, route: tabRoute(id, tab.key, { action }) })) } />
                <Switch>
                    <Route
                        path={ tabRoute(id, 'basic-info') }
                        render={ () => (
                            isEditing
                                ? <ParkingLotBasicInfoForm initialValues={ itemWithoutSchedule } />
                                : <ParkingLotBasicInfo item={ itemWithoutSchedule } />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'schedule') }
                        render={ () => (
                            isEditing
                                ? <ScheduleForm initialValues={ item } />
                                : <ScheduleDetails item={ item } />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'events') }
                        render={ () => (
                            <EventsList
                                title={ pageTitle }
                                subtitle={ t('Events') }
                                dataSelector={ events.getForParkingLot(url) }
                                dataURL={ api.buildURL('events', { parking_lot_id: id }) }
                            />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'sessions') }
                        render={ () => (
                            <DetailedSessionsList
                                title={ pageTitle }
                                subtitle={ t('Sessions') }
                                dataSelector={ sessions.getPastForParkingLot(url) }
                                dataURL={ api.buildURL('sessions', { parking_lot_id: id, active: false }) }
                                parkingLot={ item }
                            />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'payments') }
                        render={ () => (
                            <PaymentsList
                                title={ pageTitle }
                                subtitle={ t('Payments') }
                                dataSelector={ payments.getDebitsForParkingLot(url) }
                                dataURL={ api.buildURL('payments', { parking_lot_id: id, type: 'debit' }) }
                            />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'privileges') }
                        render={ () => (
                            <div>
                                <PrivilegesList
                                    title={ pageTitle }
                                    subtitle={ t('Privileges') }
                                    dataSelector={ privileges.getForParkingLot(url) }
                                    dataURL={ api.buildURL('privileges', { parking_lot_id: id }) }
                                />
                                <PrivilegeForm initialValues={ { parkingLotId: id } } />
                            </div>
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'gates') }
                        render={ () => (
                            <GatesList
                                title={ pageTitle }
                                subtitle={ t('Gates') }
                                dataSelector={ gates.getForParkingLot(url) }
                                dataURL={ api.buildURL('gates', { parking_lot_id: id }) }
                            />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'location') }
                        render={ () => (
                            isEditing
                                ? <ParkingLotMapForm initialValues={ itemWithoutSchedule } />
                                : <ParkingLotMapView item={ itemWithoutSchedule } />
                        ) }
                    />
                    <Route
                        path={ tabRoute(id, 'photo') }
                        render={ () => <ParkingLotPhotoView item={ item } isEditable={ isEditing } /> }
                    />
                    <Route
                        path={ tabRoute(id, 'promo-codes') }
                        render={ () => (
                            <div>
                                <PromoCodesList
                                    title={ pageTitle }
                                    subtitle={ t('Promo Codes') }
                                    dataSelector={ promoCodes.getForParkingLot(url) }
                                    dataURL={ api.buildURL('promoCodes', { parking_lot_id: id }) }
                                />
                                <PromoCodesForm initialValues={ {
                                    parkingLotId : id,
                                    start_moment : new Date(new Date().setHours(0, 0, 0, 0)),
                                    stop_moment  : new Date(
                                        new Date(new Date().getFullYear(), 11, 31).setHours(0, 0, 0, 0)
                                    ),
                                    enabled: true
                                } }
                                />
                            </div>
                        ) }
                    />
                    <Route
                        path={ tabRoute(id) }
                        render={ () => <ParkingLotBasicInfo item={ item } /> }
                    />
                </Switch>
            </Panel>
        </div>
    )
}

ParkingLotDetails.propTypes = {
    item    : PropTypes.object,
    tab     : PropTypes.string,
    action  : PropTypes.string,
    canEdit : PropTypes.bool
}

function mapStateToProps(state, ownProps) {
    const item = parkingLots.parsePayload(ownProps.item)
    const tab = get(ownProps, 'params.tab', 'basic-info')
    const action = get(ownProps, 'params.action')
    const canEdit = parkingLots.hasCurrentUserPrivilege(item.url, privileges.TYPES.owner)(state)

    return {
        item,
        tab,
        action,
        canEdit
    }
}

export default compose(
    translate(),
    routing.withRouter,
    connect(mapStateToProps)
)(ParkingLotDetails)
